import { createContext, useContext, useEffect, useRef, useState } from "react";
import { FirebaseContext } from "./Firebase";

import { useLocation, useNavigate } from "react-router-dom";
import { DataContext } from "./Data";
import getIframeLinkByNavlink from "../utils/getIframeLinkByNavlink";
import { handleCampaignPushResponse } from "../utils/campaignRun";

import { AstratoConfig } from "./Config";
import { LoggingContext } from "./Logger";
import { AuthContext } from "./Authorization";
const ASTRATO_URL = AstratoConfig.baseURL;
const APP_NAME = AstratoConfig.appName;

const AstratoContext = createContext();

const AstratoProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { v2Routes, space, account, callConfigAstratoConnection } = useContext(DataContext);
  const { log } = useContext(LoggingContext);
  const { functions, db } = useContext(FirebaseContext);
  const { authData, useAuth } = useContext(AuthContext);
  const { userHasUserPermission } = useAuth();
  const [ticket, setTicket] = useState();
  const [iframeSrc, setIframeSrc] = useState();
  const [itemSelected, setItemSelected] = useState();
  const [v2RoutesSelected, setV2RoutesSelected] = useState();
  const iframeRef = useRef(null);
  const campaignListenersRef = useRef(new Set()); // Firestore listeners of async campaign runs
  const [isAuthenticated, setIsAuthenticated] = useState(true);

  const itemSelectedRef = useRef(itemSelected);

  const v2RoutesSelectedRef = useRef(v2RoutesSelected);

  const accountChangedRef = useRef(account);

  const spaceRef = useRef(space);

  const authDataRef = useRef(authData);
  const lastSentPayloadRef = useRef(null);

  const stringStaging = window.location.origin.includes("staging") ? "_staging" : "";

  useEffect(() => {
    itemSelectedRef.current = itemSelected;
  }, [itemSelected]);

  useEffect(() => {
    v2RoutesSelectedRef.current = v2RoutesSelected;
  }, [v2RoutesSelected]);

  useEffect(() => {
    accountChangedRef.current = account;
  }, [account]);

  useEffect(() => {
    spaceRef.current = space;
  }, [space]);

  useEffect(() => {
    authDataRef.current = authData;
  }, [authData]);

  // Stop following async campaign runs when the provider unmounts (the runs themselves keep going in the backend).
  useEffect(() => {
    const listeners = campaignListenersRef.current;
    return () => {
      listeners.forEach((unsubscribe) => unsubscribe());
      listeners.clear();
    };
  }, []);

  const refreshUser = async () => {
    const { data } = await functions.refreshUserSession();
    setTicket(data.ticket);
    return data.ticket;
  };

  useEffect(() => {
    callConfigAstratoConnection(APP_NAME[space], account, space);
  }, []);

  useEffect(() => {
    const currentRoute = v2Routes[
      location.pathname.includes("v2/dtc") ? `dtc_routes${stringStaging}` : `depl_routes${stringStaging}`
    ]?.find((r) => r.links.some((l) => location.pathname.includes(l.navlink)));

    setV2RoutesSelected(
      v2Routes[location.pathname.includes("v2/dtc") ? `dtc_routes${stringStaging}` : `depl_routes${stringStaging}`],
    );

    if (currentRoute) {
      const currentLink = currentRoute.links.find((l) => location.pathname.includes(l.navlink));
      if (iframeSrc !== (currentLink?.iframeLink || null)) {
        setIframeSrc(currentLink?.iframeLink || null);
      }
      if (itemSelected?.label !== currentLink.label) {
        setItemSelected(currentLink);
      }
    } else if (location.pathname.includes("v2/dtc")) {
      if (v2Routes.hasOwnProperty(`dtc_routes${stringStaging}`)) {
        const result = getIframeLinkByNavlink(v2Routes[`dtc_routes${stringStaging}`], "kpi_dashboard");
        if (iframeSrc !== (result?.iframeLink || null)) {
          setIframeSrc(result?.iframeLink || null);
        }
        if (itemSelected?.label !== result.label) {
          setItemSelected(result);
        }
      }
    } else if (location.pathname.includes("v2/depl")) {
      if (v2Routes.hasOwnProperty(`depl_routes${stringStaging}`)) {
        const result = getIframeLinkByNavlink(v2Routes[`depl_routes${stringStaging}`], "kpi_dashboard");
        if (iframeSrc !== (result?.iframeLink || null)) {
          setIframeSrc(result?.iframeLink || null);
        }
        if (itemSelected?.label !== result.label) {
          setItemSelected(result);
        }
      }
    }
  }, [location, v2Routes, iframeSrc, itemSelected]);

  useEffect(() => {
    const handleMessage = (event) => {
      log.debug("# ENO # - New message from Iframe -", event.data);

      handleIncomingMessage(event.data);
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const handleAddCrmItem = async (responseType, crmType, data) => {
    log.debug(`# ENO # - ${responseType}`, accountChangedRef.current, data.payload || data.message);
    try {
      const payload = data.payload || data.message;
      const parsedPayload = typeof payload === "string" ? JSON.parse(payload) : payload;
      const vendor =
        typeof parsedPayload === "object" && parsedPayload?.vendor != null ? parsedPayload.vendor : parsedPayload;
      const { data: result } = await functions.crmBridge(crmType, { account_eid: accountChangedRef.current, vendor });
      sendMessageToIframe({ type: responseType, payload: { ...result, success: true } });
    } catch (error) {
      log.error(`# ENO # - Error in ${responseType}`, error);
      sendMessageToIframe({ type: responseType, payload: { success: false } });
    }
  };

  const handleIncomingMessage = async (data) => {
    switch (data.type) {
      /**
       * "Astrato" type is the default to refresh cookie
       */
      case "Astrato":
        if (data.payload === "401Error") {
          log.debug("# ENO # - Running 401Error function");
          await generateNewToken(data);
        }
        break;

      /**
       * Message from Astrato to send him the Sheet
       *
       * ASTRATO_NAVIGATE_GET_SHEET - Sent from Astrato before workbook opens
       */
      case "ASTRATO_NAVIGATE_GET_SHEET":
        const messageToIframeConverted = JSON.parse(
          itemSelectedRef.current.messageToIframe.replace(/(\w+):/g, '"$1":'),
        );
        log.debug("# ENO # - ASTRATO_NAVIGATE_GET_SHEET", messageToIframeConverted);
        sendMessageToIframe(messageToIframeConverted);
        createNewEntryLog({ ...data, type: "ASTRATO_OPEN_WORKBOOK" });
        break;

      /**
       * Message from Astrato to send him the Sheet
       *
       * ASTRATO_OPEN_SHEET - Sent from Astrato when there is a click button, passing the new Active Sheet
       */
      case "ASTRATO_OPEN_SHEET":
        log.debug("# ENO # - ASTRATO_OPEN_SHEET");

        getLabelByPayload(data.payload);
        createNewEntryLog(data);
        break;

      case "GET_CHIRP_BULK_MESSAGES":
        log.debug("# ENO # - GET_CHIRP_BULK_MESSAGES", accountChangedRef.current);
        try {
          const { data: dataChirp } = await functions.campaigns("list", {
            account_eid: accountChangedRef.current,
            vendor: "chirp",
          });

          sendMessageToIframe({ type: "CHIRP_BULK_MESSAGES", payload: dataChirp.data || "" });
        } catch (error) {
          log.error("# ENO # - Error getting bulk messages", error);
          sendMessageToIframe({ type: "CHIRP_BULK_MESSAGES", payload: error.message || "Something went wrong" });
        }
        break;

      case "GET_CHIRP_TEMPLATES":
        log.debug("# ENO # - GET_CHIRP_TEMPLATES", accountChangedRef.current);
        try {
          const { data: dataChirpTemplates } = await functions.campaigns("template", {
            account_eid: accountChangedRef.current,
          });

          sendMessageToIframe({ type: "CHIRP_TEMPLATES", payload: dataChirpTemplates.data || "" });
        } catch (error) {
          log.error("# ENO # - Error getting templates", error);
          sendMessageToIframe({ type: "CHIRP_TEMPLATES", payload: error.message || "Something went wrong" });
        }
        break;

      case "RUN_CAMPAIGN":
        log.debug("# ENO # - RUN_CAMPAIGN", accountChangedRef.current, data.payload || data.message);
        const objCampaign = {
          account_eid: accountChangedRef.current,
          campaign_id: data.payload || data.message,
        };
        log.debug("# ENO # - objCampaign", objCampaign);
        log.debug("# ENO # - RUNNING CAMPAIGN");
        const responseCampaign = await functions.campaigns("push", objCampaign);
        log.debug("# ENO # - responseCampaign", responseCampaign);

        // Async vendors answer { logId, state }: notify now and follow the run; sync vendors keep their old messages.
        const unsubscribeCampaign = handleCampaignPushResponse(responseCampaign, {
          db,
          send: sendMessageToIframe,
          log,
        });
        if (unsubscribeCampaign) campaignListenersRef.current.add(unsubscribeCampaign);
        break;

      case "ADD_NOTES":
        await handleAddCrmItem("ADD_NOTES_RESPONSE", "notes", data);
        break;

      case "ADD_TASKS":
        await handleAddCrmItem("ADD_TASKS_RESPONSE", "tasks", data);
        break;

      case "ADD_FLAGS":
        await handleAddCrmItem("ADD_FLAGS_RESPONSE", "flags", data);
        break;
      default:
        log.debug("# ENO # - Type is not exists.");
        break;
    }
  };

  const generateNewToken = async (data) => {
    if (data?.type === "Astrato" && data?.message === "401Error") {
      setIsAuthenticated(false);

      const res = await refreshUser();
      log.debug("# ENO # - TOKEN GENERATED", res);

      if (res !== null) {
        log.debug("# ENO # - TOKEN IN FETCH", `${ASTRATO_URL}auth/proxy/oem/ticket/${res}?embed`);
        try {
          const response = await fetch(`${ASTRATO_URL}auth/proxy/oem/ticket/${res}?embed`, {
            credentials: "include",
          });
          log.debug("# ENO # - FETCH RESPONSE", response);
          const jsonResponse = await response.json();
          log.debug("# ENO # - Authenticated");
          setIframeSrc((prev) => `${prev.split("?")[0]}`);
          setIsAuthenticated(true);
          return jsonResponse;
        } catch (error) {
          console.error("# ENO # - Error during ticket fetch:", error);
          return null;
        }
      }
    }
  };

  const userHasPermissionRef = (permission) => {
    const ad = authDataRef.current;
    if (ad?.user?.protected?.roles?.includes("GLOBAL_ADMIN")) return true;
    const roles = ad?.user?.protected?.roles?.filter((r) => r !== "AUTO_ACCOUNT_ROLE") || [];
    return roles.some((role) => ad?.rules?.[role]?.[permission] === true);
  };

  const getFirstAccessibleLink = () => {
    const routes = v2RoutesSelectedRef.current;
    if (!routes) return null;
    for (const section of routes) {
      if (!userHasPermissionRef(section.permission)) continue;
      for (const link of section.links ?? []) {
        if (link?.navlink) return link;
      }
    }
    return null;
  };

  const getLabelByPayload = (payload) => {
    const routes = v2RoutesSelectedRef.current;
    if (!routes) return;

    if (lastSentPayloadRef.current === payload) {
      lastSentPayloadRef.current = null;
      return;
    }

    for (const section of routes) {
      if (!section.links) continue;
      for (const link of section.links) {
        if (!link.messageToIframe) continue;
        try {
          const messageObj = JSON.parse(link.messageToIframe.replace(/(\w+)\s*:/g, '"$1":'));
          if (messageObj.payload !== payload) continue;

          if (userHasPermissionRef(section.permission)) {
            log.debug("# ENO # - ASTRATO_OPEN_SHEET allowed, navigating to", link.navlink);
            navigate(`${spaceRef.current}/${link.navlink}`);
          } else {
            const fallback = getFirstAccessibleLink();
            log.debug("# ENO # - ASTRATO_OPEN_SHEET denied, redirecting to", fallback?.navlink);
            if (fallback) {
              navigate(`${spaceRef.current}/${fallback.navlink}`);
              if (fallback.messageToIframe) {
                try {
                  const msg = JSON.parse(fallback.messageToIframe.replace(/(\w+)\s*:/g, '"$1":'));
                  lastSentPayloadRef.current = msg.payload;
                  sendMessageToIframe(msg);
                } catch (e) {}
              }
            }
          }
          return;
        } catch (error) {
          console.warn("Invalid JSON format in messageToIframe:", link.messageToIframe);
        }
      }
    }
  };

  const createNewEntryLog = async (data) => {
    const obj = {
      accountEID: accountChangedRef.current,
      accountName: authData.accounts[accountChangedRef.current].account_name,
      emailUser: authData.user.email,
      event_payload: data.payload,
      event_type: data.type,
      space: space,
      timestamp: new Date().toLocaleString(),
      timestampISO: new Date().toISOString(),
      userId: authData.user.id,
      userName: authData.user.name,
    };

    try {
      const res = await db.collection("astrato_logs").add(obj);
      log.debug("SAVED", res.id);
    } catch (error) {
      log.debug("ERRO SAVE LOG", error);
    }
  };

  const sendMessageToIframe = (message) => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage(message, "*");
      log.debug("# ENO # - sendMessageToIframe - Sent message to Astrato:", message);
    }
  };

  return (
    <AstratoContext.Provider
      value={{ ticket, refreshUser, iframeSrc, isAuthenticated, iframeRef, sendMessageToIframe }}
    >
      {children}
    </AstratoContext.Provider>
  );
};

export { AstratoProvider, AstratoContext };
